/**
 * Window Manager — manages detached trader windows across multiple screens
 *
 * Tracks open detached windows, restores them on page reload, and coordinates
 * window lifecycle events (open, close, move, resize).
 *
 * Storage format:
 *   {
 *     windows: [
 *       { traderId: 'trader-1', x: 100, y: 200, width: 800, height: 600, type: 'equity' }
 *     ]
 *   }
 */

export interface DetachedWindowInfo {
  traderId: string;
  type: 'equity' | 'fx' | 'bonds' | 'etf';
  x: number;
  y: number;
  width: number;
  height: number;
  windowRef?: Window | null;
}

export interface WindowState {
  windows: DetachedWindowInfo[];
}

const STORAGE_KEY = 'nigastock_detached_windows';
const WINDOW_FEATURES = 'resizable,scrollbars,width=800,height=600';

/**
 * Load window state from localStorage
 */
export function loadWindowState(): WindowState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { windows: [] };

    const parsed = JSON.parse(stored);
    if (!parsed || !Array.isArray(parsed.windows)) return { windows: [] };

    return {
      windows: parsed.windows.filter((w: any) =>
        w.traderId && w.type && typeof w.x === 'number' && typeof w.y === 'number'
      ),
    };
  } catch (error) {
    console.error('[windowManager] Failed to load window state:', error);
    return { windows: [] };
  }
}

/**
 * Save window state to localStorage
 */
export function saveWindowState(state: WindowState): void {
  try {
    // Remove windowRef before saving (not serializable)
    const toSave: WindowState = {
      windows: state.windows.map(({ windowRef, ...rest }) => rest),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('[windowManager] Failed to save window state:', error);
  }
}

/**
 * Open a new detached window for a trader
 * Returns the window reference and tracking info
 */
export function openDetachedWindow(
  traderId: string,
  type: 'equity' | 'fx' | 'bonds' | 'etf',
  x: number = 100,
  y: number = 100,
  width: number = 800,
  height: number = 600
): DetachedWindowInfo | null {
  try {
    const url = `/trader?detached=true&traderId=${encodeURIComponent(traderId)}&type=${encodeURIComponent(type)}`;
    const features = `width=${width},height=${height},left=${x},top=${y},${WINDOW_FEATURES}`;

    const windowRef = window.open(url, `trader-${traderId}`, features);

    if (!windowRef) {
      console.error('[windowManager] Failed to open window (possibly blocked by popup blocker)');
      return null;
    }

    const info: DetachedWindowInfo = {
      traderId,
      type,
      x,
      y,
      width,
      height,
      windowRef,
    };

    // Track the window
    const state = loadWindowState();
    const existingIndex = state.windows.findIndex(w => w.traderId === traderId);
    if (existingIndex >= 0) {
      state.windows[existingIndex] = info;
    } else {
      state.windows.push(info);
    }
    saveWindowState(state);

    return info;
  } catch (error) {
    console.error('[windowManager] Failed to open detached window:', error);
    return null;
  }
}

/**
 * Close a detached window and remove from tracking
 */
export function closeDetachedWindow(traderId: string): void {
  try {
    const state = loadWindowState();
    const windowInfo = state.windows.find(w => w.traderId === traderId);

    if (windowInfo?.windowRef && !windowInfo.windowRef.closed) {
      windowInfo.windowRef.close();
    }

    state.windows = state.windows.filter(w => w.traderId !== traderId);
    saveWindowState(state);
  } catch (error) {
    console.error('[windowManager] Failed to close detached window:', error);
  }
}

/**
 * Update window position/size in tracking state
 */
export function updateWindowPosition(
  traderId: string,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  try {
    const state = loadWindowState();
    const windowInfo = state.windows.find(w => w.traderId === traderId);

    if (windowInfo) {
      windowInfo.x = x;
      windowInfo.y = y;
      windowInfo.width = width;
      windowInfo.height = height;
      saveWindowState(state);
    }
  } catch (error) {
    console.error('[windowManager] Failed to update window position:', error);
  }
}

/**
 * Get all tracked detached windows
 */
export function getAllDetachedWindows(): DetachedWindowInfo[] {
  const state = loadWindowState();
  return state.windows;
}

/**
 * Get a specific detached window by trader ID
 */
export function getDetachedWindow(traderId: string): DetachedWindowInfo | undefined {
  const state = loadWindowState();
  return state.windows.find(w => w.traderId === traderId);
}

/**
 * Check if a window is currently detached
 */
export function isWindowDetached(traderId: string): boolean {
  return !!getDetachedWindow(traderId);
}

/**
 * Clear all detached window tracking
 * (used on logout or factory reset)
 */
export function clearAllDetachedWindows(): void {
  try {
    const state = loadWindowState();
    for (const windowInfo of state.windows) {
      if (windowInfo.windowRef && !windowInfo.windowRef.closed) {
        try {
          windowInfo.windowRef.close();
        } catch (e) {
          // Window already closed
        }
      }
    }
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('[windowManager] Failed to clear detached windows:', error);
  }
}
